import { useCallback, useEffect, useRef, useState } from "react";
import { CloseIcon, DownloadIcon, GalleryIcon, PlayIcon, ShareIcon } from "./Icons";
import { campaign, campaignAsset, type GalleryItem } from "../config/campaign";
import {
  aspectFor,
  fileNameFor,
  formatDuration,
  formatLabel,
  isVideo,
  mimeFor,
  shareTextFor,
  thumbSource,
} from "../lib/gallery";
import { downloadFile, shareFile } from "../lib/deliver";

type Props = {
  items: GalleryItem[];
  title: string;
  note: string;
  onToast: (kind: "success" | "error", message: string) => void;
};

/**
 * A tela que o link da bio encontra enquanto a agência não entrega as artes. Ela existe
 * por honestidade: o endereço foi divulgado e precisa dizer o que houve, em vez de abrir
 * o editor como se a pessoa tivesse errado o botão.
 */
export function GalleryEmpty({ onStudio }: { onStudio: () => void }) {
  return (
    <section className="gallery gallery--vazia" aria-label="Artes da campanha">
      <div className="gallery-empty">
        <GalleryIcon className="gallery-empty__mark" />
        <h2>As artes estão a caminho</h2>
        <p>
          Estamos preparando os materiais oficiais de {campaign.shortName} para você baixar e
          compartilhar. Volte em alguns dias.
        </p>
        <button type="button" className="primary-action" onClick={onStudio}>
          Enquanto isso, faça o seu santinho
        </button>
      </div>
    </section>
  );
}

export function Gallery({ items, title, note, onToast }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [active, setActive] = useState<GalleryItem | null>(null);
  const [busy, setBusy] = useState<"download" | "share" | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (active && !dialog.open) dialog.showModal();
    if (!active && dialog.open) dialog.close();
  }, [active]);

  // Baixar a arte cheia é uma volta na rede: sem isso, o toque em "Baixar" fica parado
  // sem explicação em conexão de campo, que é onde o app é usado.
  const fileFor = useCallback(async (item: GalleryItem) => {
    const response = await fetch(campaignAsset(item.file), { cache: "force-cache" });
    if (!response.ok) throw new Error("A arte não carregou. Tente de novo em instantes.");
    const blob = await response.blob();
    return new File([blob], fileNameFor(campaign.slug, item), { type: blob.type || mimeFor(item) });
  }, []);

  async function download(item: GalleryItem) {
    setBusy("download");
    try {
      downloadFile(await fileFor(item));
      onToast("success", `${isVideo(item) ? "Vídeo baixado" : "Arte baixada"}. Agora é só compartilhar!`);
    } catch (error) {
      onToast("error", error instanceof Error ? error.message : "Não foi possível baixar a arte.");
    } finally {
      setBusy(null);
    }
  }

  async function share(item: GalleryItem) {
    setBusy("share");
    try {
      const outcome = await shareFile(await fileFor(item), {
        title: item.label,
        text: shareTextFor(item, campaign.shareText),
        url: window.location.origin || window.location.href,
      });
      if (outcome === "copied") onToast("success", "Texto e link copiados para você compartilhar.");
    } catch {
      onToast("error", "O compartilhamento não abriu. Baixe a arte e envie pelo WhatsApp.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="gallery" aria-label={title}>
      <header className="gallery-intro">
        <h2>{title}</h2>
        <p>{note}</p>
      </header>

      <ul className="gallery-grid">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="gallery-card"
              onClick={() => setActive(item)}
              aria-label={`Ver ${item.label}`}
            >
              <span className="gallery-card__moldura">
                <img
                  className="gallery-card__image"
                  style={{ aspectRatio: aspectFor(item) }}
                  src={campaignAsset(thumbSource(item))}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                {isVideo(item) && (
                  <span className="gallery-card__video" aria-hidden="true">
                    <PlayIcon />
                    {formatDuration(item.duration)}
                  </span>
                )}
              </span>
              <span className="gallery-card__meta">
                <span className="gallery-card__label">{item.label}</span>
                <span className="gallery-card__format">{formatLabel(item.format)}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      <dialog
        ref={dialogRef}
        className="gallery-viewer"
        aria-label={active?.label || "Arte da campanha"}
        onClose={() => setActive(null)}
        onClick={(event) => {
          if (event.target === dialogRef.current) setActive(null);
        }}
      >
        {active && (
          <div className="gallery-viewer__body">
            <button
              type="button"
              className="gallery-viewer__close"
              onClick={() => setActive(null)}
              aria-label="Fechar"
            >
              <CloseIcon />
            </button>
            {isVideo(active) ? (
              // `playsInline` é o que impede o iPhone de abrir o vídeo em tela cheia
              // própria, saindo da galeria; `preload="metadata"` evita puxar 16 MB de
              // quem só abriu para ver a capa.
              <video
                key={active.id}
                className="gallery-viewer__image"
                style={{ aspectRatio: aspectFor(active) }}
                src={campaignAsset(active.file)}
                poster={campaignAsset(thumbSource(active))}
                controls
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                className="gallery-viewer__image"
                style={{
                  aspectRatio: aspectFor(active),
                  backgroundImage: `url(${campaignAsset(thumbSource(active))})`,
                }}
                src={campaignAsset(active.file)}
                alt={active.label}
              />
            )}
            <p className="gallery-viewer__label">{active.label}</p>
            <div className="gallery-actions">
              <button
                type="button"
                className="primary-action"
                onClick={() => download(active)}
                disabled={busy !== null}
              >
                <DownloadIcon /> {busy === "download" ? "Baixando..." : isVideo(active) ? "Baixar vídeo" : "Baixar imagem"}
              </button>
              <button
                type="button"
                className="icon-action"
                onClick={() => share(active)}
                disabled={busy !== null}
                aria-label="Compartilhar arte"
                title="Compartilhar arte"
              >
                <ShareIcon />
              </button>
            </div>
          </div>
        )}
      </dialog>
    </section>
  );
}
